import { useCallback, useEffect, useRef, useState } from "react";
import { api, errMsg, download, MONTHS_IT, pad, itDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Printer, Plus, Trash, CheckCircle, WarningCircle, CaretLeft, CaretRight } from "@phosphor-icons/react";

const GIORNI_IT = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];
const giornoSettimana = (iso) => GIORNI_IT[new Date(`${iso}T00:00:00`).getDay()];

export default function DisossoControl() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [days, setDays] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(null);
  const codeRef = useRef(null);
  const lotRef = useRef(null);
  const [records, setRecords] = useState([]);
  const [draft, setDraft] = useState({ code: "", lot: "" });
  const dateRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/weights/controllo-disosso/stato", { params: { year, month } });
      setDays(r.data.days || []);
    } catch (e) { toast.error(errMsg(e)); }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/weights/products", { params: { only_active: true } })
      .then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const loadRecords = useCallback(async (dateIso) => {
    const iso = dateIso || dateRef.current;
    if (!iso) return;
    const [y, m] = iso.split("-");
    try {
      const r = await api.get("/weights/controllo-disosso",
        { params: { year: Number(y), month: Number(m) } });
      setRecords(r.data || []);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);

  const apri = async (date) => {
    dateRef.current = date;
    setOpen(date);
    setDraft({ code: "", lot: "" });
    await loadRecords(date);
  };

  const aggiungi = async () => {
    const dataAttiva = dateRef.current;   // sempre la data mostrata nel titolo del modale
    if (!dataAttiva) return;
    if (!draft.code.trim() || !draft.lot.trim()) {
      toast.error("Indica codice lavorazione e numero di lotto"); return;
    }
    try {
      const p = products.find((x) => x.code === draft.code.toUpperCase());
      await api.post("/weights/controllo-disosso", {
        date: dataAttiva, code: draft.code, lot: draft.lot, description: p?.name || "" });
      setDraft({ code: "", lot: "" });
      codeRef.current?.focus();   // pronto per il record successivo, senza cliccare
      await loadRecords(dataAttiva); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const elimina = async (id) => {
    try {
      await api.delete(`/weights/controllo-disosso/${id}`);
      await loadRecords(dateRef.current); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const stampa = (ln) => download(
    `/weights/export/controllo-disosso?fmt=pdf&year=${year}&month=${month}&line=${ln}`,
    `controllo_disosso_${ln.toLowerCase()}_${year}-${pad(month)}.pdf`)
    .catch((e) => toast.error(errMsg(e)));

  const delGiorno = records.filter((r) => r.date === open);

  const spostaGiorno = (delta) => {
    const d = new Date(`${open}T00:00:00`);
    d.setDate(d.getDate() + delta);
    const iso = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    dateRef.current = iso;
    setOpen(iso);
    setDraft({ code: "", lot: "" });
    if (d.getMonth() + 1 !== month || d.getFullYear() !== year) {
      setMonth(d.getMonth() + 1); setYear(d.getFullYear());
    }
    loadRecords(iso);
    setTimeout(() => codeRef.current?.focus(), 50);
  };

  return (
    <div className="space-y-4" data-testid="disosso-control-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Controllo Disosso</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Tutti i giorni del mese con lo stato di compilazione del modulo IO-10-15-M-00-00-01
          </p>
        </div>
        <div className="mobile-stack flex flex-wrap gap-2 items-center">
          {["BOVINO", "SUINO"].map((ln) => (
            <Button key={ln} variant="outline" size="sm" className="rounded-sm"
              data-testid={`disosso-pdf-${ln.toLowerCase()}`} onClick={() => stampa(ln)}>
              <Printer size={14} className="mr-1.5" /> PDF {ln}
            </Button>
          ))}
        </div>
      </div>

      <div className="mobile-stack flex flex-wrap gap-2 items-center">
        <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
          <SelectTrigger className="h-9 w-[140px] rounded-sm" data-testid="disosso-month"><SelectValue /></SelectTrigger>
          <SelectContent>{MONTHS_IT.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="h-9 w-[105px] rounded-sm" data-testid="disosso-year"><SelectValue /></SelectTrigger>
          <SelectContent>{[year - 2, year - 1, year, year + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="rounded-sm border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="disosso-days-table">
          <thead className="bg-secondary"><tr>
            {["", "Data", "Giorno", "Stato", "Righe compilate"].map((h) => (
              <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {days.map((d) => (
              <tr key={d.date} className="border-t border-border" data-testid={`disosso-day-${d.date}`}>
                <td className="px-3 py-2 text-left">
                  <Button size="sm" className="rounded-sm h-8 font-semibold"
                    data-testid={`disosso-fill-${d.date}`} onClick={() => apri(d.date)}>
                    COMPILA
                  </Button>
                </td>
                <td className="px-3 py-2 font-semibold tabular-nums" data-testid={`disosso-date-${d.date}`}>
                  {itDate(d.date)}
                </td>
                <td className="px-3 py-2 capitalize" data-testid={`disosso-weekday-${d.date}`}>
                  {giornoSettimana(d.date)}
                </td>
                <td className="px-3 py-2">
                  {d.compiled ? (
                    <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-600"
                      data-testid={`disosso-state-${d.date}`}>
                      <CheckCircle size={16} /> COMPILATO
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 font-semibold text-amber-600"
                      data-testid={`disosso-state-${d.date}`}>
                      <WarningCircle size={16} /> DA COMPILARE
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 tabular-nums">{d.records}</td>
              </tr>
            ))}
            {days.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-muted-foreground" data-testid="disosso-empty">
                Nessun giorno da mostrare per il mese selezionato
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!open} onOpenChange={(o) => { if (!o) { setOpen(null); dateRef.current = null; } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="disosso-fill-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Button variant="outline" size="icon" className="h-7 w-7 rounded-sm" data-testid="disosso-prev-day"
                onClick={() => spostaGiorno(-1)} title="Giorno precedente">
                <CaretLeft size={14} />
              </Button>
              <span>Controllo disosso — {open ? `${giornoSettimana(open)} ${itDate(open)}` : ""}</span>
              <Button variant="outline" size="icon" className="h-7 w-7 rounded-sm" data-testid="disosso-next-day"
                onClick={() => spostaGiorno(1)} title="Giorno successivo">
                <CaretRight size={14} />
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
              <div>
                <Label className="text-xs">Codice lavorazione</Label>
                <Input value={draft.code} list="disosso-codes" className="h-9 rounded-sm mt-1" ref={codeRef}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lotRef.current?.focus(); } }}
                  data-testid="disosso-code" onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })} />
                <datalist id="disosso-codes">
                  {products.map((p) => <option key={p.id || p.code} value={p.code}>{p.name}</option>)}
                </datalist>
              </div>
              <div>
                <Label className="text-xs">N° macellazione/lotto</Label>
                <Input value={draft.lot} className="h-9 rounded-sm mt-1" data-testid="disosso-lot" ref={lotRef}
                  onKeyDown={(e) => e.key === "Enter" && aggiungi()}
                  onChange={(e) => setDraft({ ...draft, lot: e.target.value })} />
              </div>
              <Button size="sm" className="rounded-sm font-semibold h-9" data-testid="disosso-add" onClick={aggiungi}>
                <Plus size={14} className="mr-1.5" /> Aggiungi record
              </Button>
            </div>

            <div className="rounded-sm border border-border">
              <table className="w-full text-sm bc-no-cards" data-testid="disosso-records-table">
                <thead className="bg-secondary"><tr>
                  {["", "Codice", "Descrizione", "Linea", "Lotto"].map((h) => (
                    <th key={h} className="px-2 py-1.5 text-left text-[11px] font-semibold uppercase text-muted-foreground">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {delGiorno.map((r) => (
                    <tr key={r.id} className="border-t border-border" data-testid={`disosso-record-${r.id}`}>
                      <td className="px-2 py-1.5 text-left">
                        <button type="button" className="text-muted-foreground hover:text-rose-500"
                          data-testid={`disosso-record-delete-${r.id}`} onClick={() => elimina(r.id)}>
                          <Trash size={14} />
                        </button>
                      </td>
                      <td className="px-2 py-1.5 font-semibold">{r.code}</td>
                      <td className="px-2 py-1.5 text-xs text-muted-foreground">{r.description}</td>
                      <td className="px-2 py-1.5 text-xs font-semibold">{r.line}</td>
                      <td className="px-2 py-1.5 tabular-nums">{r.lot}</td>
                    </tr>
                  ))}
                  {delGiorno.length === 0 && (
                    <tr><td colSpan={5} className="px-2 py-4 text-muted-foreground text-sm">Nessun record inserito</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" data-testid="disosso-close"
              onClick={() => { setOpen(null); dateRef.current = null; }}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
